import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import { useDrop } from 'react-dnd';
import { formatSeconds } from '@playbookux/playbook-web-core';

import { dragDropTypes } from '../../../../constants/drag-drop-types';
import VideoPlayer, { withMedia } from '../../../../../core/components/video-player';
import HangTight from '../../../../../core/components/hang-tight';
import ControlBarSpacer from '../../../../../core/components/video-player/control-bar-spacer';

import {
  Container,
  DragDropComponent,
  FeedbackMessage,
  Header,
  Input,
  Message,
  ContainerPlayer,
} from './styles';

import BlankVideo from './blank-video';
import ClipBottom from './clip-bottom';

const ERROR_MESSAGE = "We're not able to process this clip. Please contact support.";
const HANG_TIGHT_MESSAGE = 'Hang tight! We’re generating your highlight reel';

const ContainerVideo = (props) => {
  const {
    clips,
    feedback,
    title,
    status,
    totalClips,
    videoUrl,
    onAddClip,
    onMoveClip,
    onRemoveClip,
    onTitleChange,
    onShowClip,
    media,
  } = props;

  const showVideoPlayer = status === 'processed';
  const processingError = ['error', 'transcribe_error'].includes(status);
  const showBlackComponent = !showVideoPlayer || processingError;

  const showBlankVideo = !totalClips;

  const handleDrop = (item, index) => {
    if (item.type === dragDropTypes.CLIP_BOTTOM) {
      const oldIndex = item.index;
      const newIndex = (index > oldIndex) ? index - 1 : index;
      if (oldIndex !== newIndex) {
        onMoveClip(oldIndex, newIndex);
      }
    } else if (item.type === dragDropTypes.CARD_CLIP) {
      onAddClip(item.testSessionNote, index);
    }
  };

  const [, drop] = useDrop({
    accept: [dragDropTypes.CLIP_BOTTOM, dragDropTypes.CARD_CLIP],
    drop: (item, monitor) => {
      const { index } = monitor.getDropResult() || { index: clips.length };
      handleDrop(item, index);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const hasFeedback = !_.isEmpty(feedback);

  const handleShowClip = (clipId) => {
    onShowClip(clipId);
  };

  const { currentTime } = media;

  // Find current playing clip
  const currentClip = useMemo(() => {
    let totalTime = 0;
    let playingClip;

    clips.forEach((clip) => {
      const { testSessionNote } = clip;

      const {
        id,
        videoStartTime,
        videoEndTime,
      } = testSessionNote;

      const clipLength = videoEndTime - videoStartTime;

      if (currentTime > totalTime && currentTime < (totalTime + clipLength)) {
        playingClip = id;
      }
      totalTime += clipLength;
    });
    return playingClip;
  }, [currentTime]);

  return (
    <Container>
      <Header>
        <Input
          onChange={onTitleChange}
          value={title}
        />

        {hasFeedback && (
          <FeedbackMessage>
            {feedback}
          </FeedbackMessage>
        )}
      </Header>

      {showBlackComponent && !showBlankVideo && (
        <ContainerPlayer>
          <HangTight
            processingError={processingError}
            hangTightMessage={HANG_TIGHT_MESSAGE}
            errorMessage={ERROR_MESSAGE}
          />
        </ContainerPlayer>
      )}

      {showVideoPlayer && !showBlankVideo && (
        <ContainerPlayer>
          <VideoPlayer src={videoUrl}>
            <ControlBarSpacer
              beforeElement="downloadButton"
            />
          </VideoPlayer>
        </ContainerPlayer>
      )}

      {showBlankVideo && (
        <BlankVideo />
      )}

      <DragDropComponent ref={drop}>
        {clips.map((clip, index) => {
          const { testSessionNote } = clip;

          const {
            id,
            note,
            testSession,
            videoStartTime,
            videoEndTime,
            tagHighlights,
          } = testSessionNote;

          const { tester } = testSession;
          const { user, project } = tester;

          return (
            <ClipBottom
              index={index}
              key={id}
              id={id}
              note={note}
              onRemoveClip={onRemoveClip}
              time={formatSeconds(videoEndTime - videoStartTime)}
              title={project.name}
              user={user.profile.firstName}
              onClickPlay={handleShowClip}
              tagHighlights={tagHighlights}
              isPlaying={currentClip === id}
            />
          );
        })}

        {_.isEmpty(clips) && (
          <Message>
            Drag & Reorder your clips here
          </Message>
        )}
      </DragDropComponent>
    </Container>
  );
};

ContainerVideo.propTypes = {
  clips: PropTypes.arrayOf(PropTypes.shape({
    testSessionNote: PropTypes.shape({
      id: PropTypes.string,
      note: PropTypes.string,
      testSession: PropTypes.shape({
        tester: PropTypes.shape({
          user: PropTypes.shape({}),
          project: PropTypes.shape({}),
        }),
      }),
      videoStartTime: PropTypes.number,
      videoEndTime: PropTypes.number,
      tagHighlights: PropTypes.shape({}),
    }),
  })).isRequired,
  feedback: PropTypes.string,
  onAddClip: PropTypes.func.isRequired,
  onMoveClip: PropTypes.func.isRequired,
  onRemoveClip: PropTypes.func.isRequired,
  onTitleChange: PropTypes.func.isRequired,
  title: PropTypes.string,
  status: PropTypes.string.isRequired,
  totalClips: PropTypes.number.isRequired,
  videoUrl: PropTypes.string.isRequired,
  onShowClip: PropTypes.func.isRequired,
  media: PropTypes.shape({
    currentTime: PropTypes.number,
  }).isRequired,
};

ContainerVideo.defaultProps = {
  feedback: '',
  title: '',
};

export default withMedia(ContainerVideo);
